import { format, startOfDay } from 'date-fns';
import { isWithinInterval } from 'date-fns/isWithinInterval';
import { parseISO } from 'date-fns/parseISO';
import { subHours } from 'date-fns/subHours';

/**
 * The today's date.
 * @returns The today's date. eg new Date(2025, 0, 1)
 */
export const today = startOfDay(new Date());

/**
 * Checks if the date is within the last 24 hours.
 * @param date - The date to check. Date object. eg new Date()
 * @returns True if the date is within the last 24 hours, false otherwise.
 */
export function isWithinLast24Hours(date: Date) {
  const nowMinus24h = subHours(new Date(), 24);
  return isWithinInterval(date, {
    start: nowMinus24h,
    end: new Date(),
  });
}

/**
 * Converts the date to a Date object.
 * @param date - ISO string or Date object. eg 2025-01-01T00:00:00.000Z or new Date(2025, 0, 1)
 * @returns The Date object. eg new Date(2025, 0, 1)
 */
export function toDate(date: string | Date | null) {
  return date ? (date instanceof Date ? date : parseISO(date)) : null;
}

/**
 * Formats the date to the short date format.
 * @param date - ISO string or Date object. eg 2025-01-01T00:00:00.000Z or new Date(2025, 0, 1)
 * @returns The formatted date. eg Jan 1, 2025
 */
export function formatShortDate(date: Date | string): string {
  return format(date, 'LLL dd, y');
}

/**
 * Calculates the duration in days between two dates.
 * @param startDate - The start date. ISO string or Date object. eg 2025-01-01T00:00:00.000Z or new Date(2025, 0, 1)
 * @param endDate - The end date. ISO string or Date object. eg 2025-01-01T00:00:00.000Z or new Date(2025, 0, 1)
 * @returns The duration in days. eg 30
 */
export function durationInDays(
  startDate: string | Date,
  endDate: string | Date,
) {
  return Math.abs(
    Math.round(
      (new Date(endDate).getTime() - new Date(startDate).getTime()) /
        (1000 * 60 * 60 * 24),
    ),
  );
}
